// Pine Script recursive-descent parser

import type {
  BinaryOp,
  BoolLit,
  CallExpr,
  Expr,
  ExprStmt,
  IfStmt,
  IndicatorDecl,
  NaLit,
  NumberLit,
  Program,
  SeriesRef,
  Stmt,
  StringLit,
  TernaryExpr,
  TupleAssign,
  UnaryExpr,
  VarAssign,
  VersionDecl,
} from '../ast/index'
import type { Token, TokenKind } from '../lexer/index'

export class ParseError extends Error {
  constructor(
    message: string,
    public line: number,
    public col: number
  ) {
    super(`[${line}:${col}] ${message}`)
  }
}

// ── Parser class ──────────────────────────────────────────────────────────────

class Parser {
  private pos = 0

  constructor(private tokens: Token[]) {}

  private peek(): Token {
    return this.tokens[this.pos] ?? { kind: 'EOF', value: '', pos: { line: 0, col: 0 } }
  }

  private peekKind(): TokenKind {
    return this.peek().kind
  }

  private advance(): Token {
    const t = this.peek()
    this.pos++
    return t
  }

  private eat(kind: TokenKind): Token {
    const t = this.peek()
    if (t.kind !== kind) {
      throw new ParseError(
        `Expected ${kind} but got ${t.kind} (${JSON.stringify(t.value)})`,
        t.pos.line,
        t.pos.col
      )
    }
    return this.advance()
  }

  private skip(kind: TokenKind): boolean {
    if (this.peekKind() === kind) {
      this.advance()
      return true
    }
    return false
  }

  private skipNewlines() {
    while (this.peekKind() === 'NEWLINE') this.advance()
  }

  // ── Program ───────────────────────────────────────────────────────────────

  parseProgram(): Program {
    const stmts: Stmt[] = []
    this.skipNewlines()
    while (this.peekKind() !== 'EOF') {
      const stmt = this.parseStmt()
      if (stmt) stmts.push(stmt)
      this.skipNewlines()
    }
    return { stmts }
  }

  // ── Statements ────────────────────────────────────────────────────────────

  private parseStmt(): Stmt | null {
    const t = this.peek()

    // Skip version comments and plain comments
    if (t.kind === 'VERSION') {
      const pos = t.pos
      const version = parseInt(this.advance().value)
      this.skip('NEWLINE')
      const decl: VersionDecl = { kind: 'VersionDecl', version, pos }
      return decl
    }
    if (t.kind === 'COMMENT') {
      this.advance()
      this.skip('NEWLINE')
      return null
    }
    if (t.kind === 'NEWLINE') {
      this.advance()
      return null
    }

    // Tuple assignment: [a, b, c] = expr
    if (t.kind === 'LBRACKET') {
      return this.parseTupleAssign()
    }

    // IDENT followed by = or :=  → VarAssign
    // IDENT followed by anything else → ExprStmt
    if (t.kind === 'IDENT') {
      // Look ahead to see if this is an assignment
      const saved = this.pos
      const name = this.advance().value
      const next = this.peekKind()
      if (next === 'EQ' || next === 'WALRUS') {
        this.advance() // consume = or :=
        const value = this.parseExpr()
        this.skip('NEWLINE')
        const assign: VarAssign = { kind: 'VarAssign', name, value, pos: t.pos }
        return assign
      }
      // Not an assignment; restore and parse as expression
      this.pos = saved
    }

    // if statement
    if (t.kind === 'IDENT' && t.value === 'if') {
      return this.parseIfStmt()
    }

    // Expression statement (plot(), indicator(), etc.)
    const expr = this.parseExpr()
    this.skip('NEWLINE')
    const exprStmt: ExprStmt = { kind: 'ExprStmt', expr, pos: t.pos }

    // Convert indicator(...) call to IndicatorDecl
    if (expr.kind === 'CallExpr' && expr.callee[0] === 'indicator') {
      return this.callToIndicatorDecl(expr)
    }

    return exprStmt
  }

  private parseTupleAssign(): TupleAssign {
    const pos = this.peek().pos
    this.eat('LBRACKET')
    const names: string[] = []
    while (this.peekKind() !== 'RBRACKET' && this.peekKind() !== 'EOF') {
      if (this.peekKind() === 'IDENT') {
        names.push(this.advance().value)
      } else {
        this.advance() // skip commas etc.
      }
      this.skip('COMMA')
    }
    this.eat('RBRACKET')
    const op = this.peekKind()
    if (op !== 'EQ' && op !== 'WALRUS') {
      throw new ParseError(
        'Expected = after tuple target',
        this.peek().pos.line,
        this.peek().pos.col
      )
    }
    this.advance()
    const value = this.parseExpr()
    this.skip('NEWLINE')
    return { kind: 'TupleAssign', names, value, pos }
  }

  private parseIfStmt(): IfStmt {
    const pos = this.peek().pos
    this.eat('IDENT') // 'if'
    const condition = this.parseExpr()
    this.skip('NEWLINE')

    // Indented body
    const consequent = this.parseBlock()

    let alternate: Stmt[] | null = null
    // Check for else
    const peeked = this.peek()
    if (peeked.kind === 'IDENT' && peeked.value === 'else') {
      this.advance()
      this.skip('NEWLINE')
      alternate = this.parseBlock()
    }

    return { kind: 'IfStmt', condition, consequent, alternate, pos }
  }

  private parseBlock(): Stmt[] {
    const stmts: Stmt[] = []
    if (this.peekKind() !== 'INDENT') return stmts
    this.eat('INDENT')
    while (this.peekKind() !== 'DEDENT' && this.peekKind() !== 'EOF') {
      const s = this.parseStmt()
      if (s) stmts.push(s)
    }
    this.skip('DEDENT')
    return stmts
  }

  private callToIndicatorDecl(expr: CallExpr): IndicatorDecl {
    let title = ''
    let shortTitle: string | undefined
    let overlay = false

    // positional args
    if (expr.args[0]?.kind === 'StringLit') title = expr.args[0].value
    // kwargs
    for (const kw of expr.kwargs) {
      if (kw.key === 'overlay' && kw.value.kind === 'BoolLit') {
        overlay = kw.value.value
      }
      if (kw.key === 'shorttitle' && kw.value.kind === 'StringLit') {
        shortTitle = kw.value.value
      }
    }
    return { kind: 'IndicatorDecl', title, shortTitle, overlay, pos: expr.pos }
  }

  // ── Expressions (precedence climbing) ────────────────────────────────────

  parseExpr(): Expr {
    return this.parseTernary()
  }

  private parseTernary(): Expr {
    let expr = this.parseOr()
    if (this.peekKind() === 'QUESTION') {
      const pos = this.peek().pos
      this.advance()
      const consequent = this.parseOr()
      this.eat('COLON')
      const alternate = this.parseTernary()
      const ternary: TernaryExpr = {
        kind: 'TernaryExpr',
        condition: expr,
        consequent,
        alternate,
        pos,
      }
      expr = ternary
    }
    return expr
  }

  private parseOr(): Expr {
    let left = this.parseAnd()
    while (this.peekKind() === 'OR') {
      const pos = this.peek().pos
      this.advance()
      const right = this.parseAnd()
      left = { kind: 'BinaryExpr', op: 'or', left, right, pos }
    }
    return left
  }

  private parseAnd(): Expr {
    let left = this.parseNot()
    while (this.peekKind() === 'AND') {
      const pos = this.peek().pos
      this.advance()
      const right = this.parseNot()
      left = { kind: 'BinaryExpr', op: 'and', left, right, pos }
    }
    return left
  }

  private parseNot(): Expr {
    if (this.peekKind() === 'NOT') {
      const pos = this.peek().pos
      this.advance()
      const operand = this.parseNot()
      const unary: UnaryExpr = { kind: 'UnaryExpr', op: 'not', operand, pos }
      return unary
    }
    return this.parseComparison()
  }

  private parseComparison(): Expr {
    let left = this.parseAddSub()
    const compOps: TokenKind[] = ['EQEQ', 'NEQ', 'LT', 'GT', 'LTE', 'GTE']
    const opMap: Partial<Record<TokenKind, BinaryOp>> = {
      EQEQ: '==',
      NEQ: '!=',
      LT: '<',
      GT: '>',
      LTE: '<=',
      GTE: '>=',
    }
    while (compOps.includes(this.peekKind())) {
      const pos = this.peek().pos
      const op = opMap[this.advance().kind]!
      const right = this.parseAddSub()
      left = { kind: 'BinaryExpr', op, left, right, pos }
    }
    return left
  }

  private parseAddSub(): Expr {
    let left = this.parseMulDiv()
    while (this.peekKind() === 'PLUS' || this.peekKind() === 'MINUS') {
      const pos = this.peek().pos
      const op: BinaryOp = this.advance().kind === 'PLUS' ? '+' : '-'
      const right = this.parseMulDiv()
      left = { kind: 'BinaryExpr', op, left, right, pos }
    }
    return left
  }

  private parseMulDiv(): Expr {
    let left = this.parseUnary()
    while (['STAR', 'SLASH', 'PERCENT', 'CARET'].includes(this.peekKind())) {
      const pos = this.peek().pos
      const opMap: Partial<Record<TokenKind, BinaryOp>> = {
        STAR: '*',
        SLASH: '/',
        PERCENT: '%',
        CARET: '^',
      }
      const op = opMap[this.advance().kind]!
      const right = this.parseUnary()
      left = { kind: 'BinaryExpr', op, left, right, pos }
    }
    return left
  }

  private parseUnary(): Expr {
    if (this.peekKind() === 'MINUS') {
      const pos = this.peek().pos
      this.advance()
      const operand = this.parsePostfix()
      const unary: UnaryExpr = { kind: 'UnaryExpr', op: '-', operand, pos }
      return unary
    }
    return this.parsePostfix()
  }

  private parsePostfix(): Expr {
    let expr = this.parsePrimary()
    // Historical ref: expr[offset]
    while (this.peekKind() === 'LBRACKET') {
      const pos = this.peek().pos
      this.advance()
      const offset = this.parseExpr()
      this.eat('RBRACKET')
      const ref: SeriesRef = { kind: 'SeriesRef', series: expr, offset, pos }
      expr = ref
    }
    return expr
  }

  private parsePrimary(): Expr {
    const t = this.peek()

    // Number
    if (t.kind === 'NUMBER') {
      this.advance()
      const num: NumberLit = { kind: 'NumberLit', value: parseFloat(t.value), pos: t.pos }
      return num
    }

    // String
    if (t.kind === 'STRING') {
      this.advance()
      const str: StringLit = { kind: 'StringLit', value: t.value, pos: t.pos }
      return str
    }

    // Bool
    if (t.kind === 'BOOL') {
      this.advance()
      const b: BoolLit = { kind: 'BoolLit', value: t.value === 'true', pos: t.pos }
      return b
    }

    // na
    if (t.kind === 'NA') {
      this.advance()
      const na: NaLit = { kind: 'NaLit', pos: t.pos }
      return na
    }

    // Parenthesized
    if (t.kind === 'LPAREN') {
      this.advance()
      this.skipNewlines()
      const expr = this.parseExpr()
      this.skipNewlines()
      this.eat('RPAREN')
      return expr
    }

    // Identifier or dotted call
    if (t.kind === 'IDENT') {
      return this.parseIdentOrCall()
    }

    throw new ParseError(
      `Unexpected token ${t.kind} (${JSON.stringify(t.value)})`,
      t.pos.line,
      t.pos.col
    )
  }

  private parseIdentOrCall(): Expr {
    const pos = this.peek().pos
    const parts: string[] = []

    // Collect dotted parts: ta.sma or input.int
    parts.push(this.eat('IDENT').value)
    while (this.peekKind() === 'DOT') {
      this.advance()
      parts.push(this.eat('IDENT').value)
    }

    // If followed by ( — it's a function call
    if (this.peekKind() === 'LPAREN') {
      this.advance()
      this.skipNewlines()
      const args: Expr[] = []
      const kwargs: { key: string; value: Expr }[] = []

      while (this.peekKind() !== 'RPAREN' && this.peekKind() !== 'EOF') {
        this.skipNewlines()
        // Check for kwarg: ident = expr
        if (this.peekKind() === 'IDENT') {
          const saved = this.pos
          const name = this.advance().value
          if (this.peekKind() === 'EQ') {
            this.advance()
            const val = this.parseExpr()
            kwargs.push({ key: name, value: val })
          } else {
            // Not a kwarg — restore and parse as positional
            this.pos = saved
            args.push(this.parseExpr())
          }
        } else {
          args.push(this.parseExpr())
        }
        this.skipNewlines()
        if (!this.skip('COMMA')) break
        this.skipNewlines()
      }
      this.skipNewlines()
      this.eat('RPAREN')

      const call: CallExpr = { kind: 'CallExpr', callee: parts, args, kwargs, pos }
      return call
    }

    // Just an identifier
    if (parts.length === 1) {
      return { kind: 'Identifier', name: parts[0], pos }
    }

    // Dotted identifier without call (e.g. color.red) — treat as identifier
    return { kind: 'Identifier', name: parts.join('.'), pos }
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

export function parse(tokens: Token[]): Program {
  const p = new Parser(tokens)
  return p.parseProgram()
}
